import { useQueryClient } from "@tanstack/react-query";
import { doc, getFirestore, runTransaction, setDoc } from "firebase/firestore";
import {
  type Dispatch,
  type SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { toast } from "react-toastify";
import {
  useAuthState,
  useClientesActions,
  useRutasActions,
  useRutasState,
} from "../../../context/AppContext";
import { queryKeys } from "../../../queries/queryKeys";
import { formatearNumeros } from "../../../utils/formatters";
import {
  aplicarMovimientoARutaLocal,
  construirActualizacionRutaConMovimiento,
  crearMovimientoRutaData,
  crearMovimientoRutaRef,
  type MovimientoRutaCompatible,
} from "../../../utils/rutaMovimientos";
import type { AbonoCliente } from "../../modals/AbonoModal/types";
import type {
  MoveBlockedModalState,
  TablaClienteItem,
  TablaClientesModalType,
  TablaClientesProps,
  TablaClientesRutaOption,
  UseTablaClientesReturn,
} from "./types";

const normalizarNumero = (value: number | string | null | undefined): number => {
  const parsedValue = Number(value ?? 0);

  return Number.isNaN(parsedValue) ? 0 : parsedValue;
};

const obtenerNombreRuta = (nombreRuta: string | null | undefined, rutaUid: string): string => {
  const normalizedName = String(nombreRuta ?? "").trim();

  return normalizedName || rutaUid;
};

const clienteTienePagosRegistrados = (cliente: Record<string, any>): boolean => {
  const historialPagos = Array.isArray(cliente.historialPagos) ? cliente.historialPagos : [];

  return (
    historialPagos.length > 0 ||
    normalizarNumero(cliente.abono) > 0 ||
    normalizarNumero(cliente.cuotasPagadas) > 0 ||
    normalizarNumero(cliente.totalAbono) > 0 ||
    normalizarNumero(cliente.valorPico) > 0
  );
};

const crearAllClientsPayloadDesdeCliente = (
  cliente: Record<string, any>,
  clienteUid: string,
  createdAt: Date,
) => ({
  clienteUid,
  ultimoCreditoUid: clienteUid,
  createdAt: cliente.createdAt ?? createdAt,
  cpf: cliente.cpf ?? "",
  nombreCliente: cliente.nombreCliente ?? "",
  direccionCobro: cliente.direccionCobro ?? "",
  telefono: cliente.telefono ?? "",
  direccion: cliente.direccion ?? "",
  descripcion: cliente.descripcion ?? "",
  ubicacion: cliente.ubicacion ?? "",
  imageUrl: cliente.imageUrl ?? null,
  imageTienda: cliente.imageTienda ?? null,
});

class MoveClienteValidationError extends Error {
  moveBlockedModal: MoveBlockedModalState | null;

  constructor(message: string, moveBlockedModal: MoveBlockedModalState | null = null) {
    super(message);
    this.name = "MoveClienteValidationError";
    this.moveBlockedModal = moveBlockedModal;
  }
}

type ResultadoTransaccionMoverCliente = {
  clienteTrasladado: TablaClienteItem;
  movimientoDestino: MovimientoRutaCompatible;
  movimientoOrigen: MovimientoRutaCompatible;
  nombreRutaDestino: string;
  rutaDestinoUid: string;
  rutaOrigenUid: string;
  saldoDestinoNuevo: number;
  saldoOrigenNuevo: number;
};

export const useTablaClientes = ({
  datos,
  searchTerm,
  setUsuarioRuta,
  usuarioRuta,
}: TablaClientesProps): UseTablaClientesReturn => {
  const [activeModal, setActiveModal] = useState<TablaClientesModalType | null>(null);
  const [selectedCliente, setSelectedCliente] = useState<TablaClienteItem | null>(null);
  const [clientes, setClientes] = useState<TablaClienteItem[]>(datos ?? []);
  const [isMovingClient, setIsMovingClient] = useState(false);
  const [moveBlockedModal, setMoveBlockedModal] = useState<MoveBlockedModalState | null>(null);
  const [orderValues, setOrderValues] = useState<Record<string, number>>({});
  const [isModalPreguntaRenovacion, setIsModalPreguntaRenovacion] = useState(false);
  const [inputValues, setInputValues] = useState<Record<string, number>>({});
  const { userData } = useAuthState();
  const { setInfoClientes } = useClientesActions();
  const { setRutasData, setUsuarioRuta: setContextUsuarioRuta } = useRutasActions();
  const { rutasData, usuarioRuta: contextUsuarioRuta } = useRutasState();
  const queryClient = useQueryClient();

  useEffect(() => {
    setClientes(datos ?? []);
  }, [datos]);

  const rutasFiltradas = useMemo(
    () =>
      ((rutasData ?? []) as TablaClientesRutaOption[]).filter(
        (ruta) => ruta.uid !== usuarioRuta?.uid,
      ),
    [rutasData, usuarioRuta?.uid],
  );

  const filteredClients = useMemo(() => {
    const normalizedSearchTerm = searchTerm.toLowerCase();

    return clientes.filter(
      (cliente) =>
        Boolean(cliente?.nombreCliente) &&
        cliente.nombreCliente.toLowerCase().includes(normalizedSearchTerm),
    );
  }, [clientes, searchTerm]);

  const showAdminActions = Boolean(userData?.isAdmin);
  const showMoveColumn = Boolean(userData && rutasFiltradas.length >= 1);

  const setModalState = useCallback(
    (modal: TablaClientesModalType, value: SetStateAction<boolean>): void => {
      setActiveModal((currentModal) => {
        const isCurrentModalActive = currentModal === modal;
        const nextValue = typeof value === "function" ? value(isCurrentModalActive) : value;

        if (nextValue) {
          return modal;
        }

        return isCurrentModalActive ? null : currentModal;
      });
    },
    [],
  );

  const setIsAbono: Dispatch<SetStateAction<boolean>> = useCallback(
    (value) => {
      setModalState("abono", value);
    },
    [setModalState],
  );

  const setIsModalDelete: Dispatch<SetStateAction<boolean>> = useCallback(
    (value) => {
      setModalState("delete", value);
    },
    [setModalState],
  );

  const setVerDetallesCliente: Dispatch<SetStateAction<boolean>> = useCallback(
    (value) => {
      setModalState("detalles", value);
    },
    [setModalState],
  );

  const setIsModalEdit: Dispatch<SetStateAction<boolean>> = useCallback(
    (value) => {
      setModalState("edit", value);
    },
    [setModalState],
  );

  const closeMoveBlockedModal = useCallback(() => {
    setMoveBlockedModal(null);
  }, []);

  const openClienteModal = useCallback(
    (modal: TablaClientesModalType, cliente: TablaClienteItem): void => {
      setSelectedCliente(cliente);
      setActiveModal(modal);
    },
    [],
  );

  const handleChangeOrder = useCallback((clienteUid: string, newOrder: number): void => {
    if (!Number.isFinite(newOrder) || newOrder < 0) {
      setOrderValues((prevOrderValues) => {
        const nextOrderValues = { ...prevOrderValues };
        delete nextOrderValues[clienteUid];
        return nextOrderValues;
      });

      setInputValues((prevInputValues) => {
        const nextInputValues = { ...prevInputValues };
        delete nextInputValues[clienteUid];
        return nextInputValues;
      });

      return;
    }

    setOrderValues((prevOrderValues) => ({
      ...prevOrderValues,
      [clienteUid]: newOrder,
    }));

    setInputValues((prevInputValues) => ({
      ...prevInputValues,
      [clienteUid]: newOrder + 1,
    }));
  }, []);

  const handleButtonClick = useCallback(async (): Promise<void> => {
    if (!usuarioRuta?.adminUid || !usuarioRuta.uid) {
      return;
    }

    const orderEntries = Object.entries(orderValues).filter(
      ([, newOrder]) => Number.isInteger(newOrder) && newOrder >= 0,
    );

    if (orderEntries.length === 0) {
      return;
    }

    try {
      const db = getFirestore();
      const rutaRef = doc(db, "admin_users", usuarioRuta.adminUid, "rutas", usuarioRuta.uid);

      const updatePromises = orderEntries.map(async ([clienteUid, newOrder]) => {
        const clienteRef = doc(rutaRef, "clientes", clienteUid);

        await setDoc(clienteRef, { posicion: newOrder + 1 }, { merge: true });
      });

      await Promise.all(updatePromises);

      setClientes((prevClientes) => {
        const newClientes = [...prevClientes];

        orderEntries.forEach(([clienteUid, newOrder]) => {
          const clienteIndex = newClientes.findIndex((client) => client.uid === clienteUid);

          if (clienteIndex === -1) {
            return;
          }

          newClientes[clienteIndex] = {
            ...newClientes[clienteIndex],
            posicion: newOrder + 1,
          };

          newClientes.splice(newOrder, 0, newClientes.splice(clienteIndex, 1)[0]);
        });

        return newClientes;
      });

      setInputValues({});
      setOrderValues({});
    } catch (error) {
      console.error("Error al actualizar la posicion del cliente:", error);
    }
  }, [orderValues, usuarioRuta]);

  const handleMoveCliente = useCallback(
    async (cliente: TablaClienteItem, nuevaRutaUid: string): Promise<void> => {
      const adminUid = usuarioRuta?.adminUid ?? userData?.uid;

      if (
        isMovingClient ||
        !adminUid ||
        !usuarioRuta?.uid ||
        !cliente.uid ||
        nuevaRutaUid === usuarioRuta.uid
      ) {
        return;
      }

      setIsMovingClient(true);
      const db = getFirestore();

      try {
        const rutaOrigenRef = doc(db, "admin_users", adminUid, "rutas", usuarioRuta.uid);
        const rutaDestinoRef = doc(db, "admin_users", adminUid, "rutas", nuevaRutaUid);
        const clienteOrigenRef = doc(
          db,
          "admin_users",
          adminUid,
          "rutas",
          usuarioRuta.uid,
          "clientes",
          cliente.uid,
        );
        const clienteDestinoRef = doc(
          db,
          "admin_users",
          adminUid,
          "rutas",
          nuevaRutaUid,
          "clientes",
          cliente.uid,
        );
        const movimientoOrigenRef = crearMovimientoRutaRef(db, adminUid, usuarioRuta.uid);
        const movimientoDestinoRef = crearMovimientoRutaRef(db, adminUid, nuevaRutaUid);
        const fechaMovimiento = new Date();
        const resultadoTransaccion = await runTransaction(
          db,
          async (transaction): Promise<ResultadoTransaccionMoverCliente> => {
            const [
              rutaOrigenSnapshot,
              rutaDestinoSnapshot,
              clienteOrigenSnapshot,
              clienteDestinoSnapshot,
            ] = await Promise.all([
              transaction.get(rutaOrigenRef),
              transaction.get(rutaDestinoRef),
              transaction.get(clienteOrigenRef),
              transaction.get(clienteDestinoRef),
            ]);
            const rutaOrigenData = rutaOrigenSnapshot.data() as Record<string, any> | undefined;
            const rutaDestinoData = rutaDestinoSnapshot.data() as Record<string, any> | undefined;
            const clienteOrigenData = clienteOrigenSnapshot.data() as
              | Record<string, any>
              | undefined;

            if (!rutaOrigenSnapshot.exists() || !rutaOrigenData) {
              throw new MoveClienteValidationError("No se pudo obtener la ruta origen.");
            }

            if (!rutaDestinoSnapshot.exists() || !rutaDestinoData) {
              throw new MoveClienteValidationError("No se pudo obtener la ruta destino.");
            }

            if (!clienteOrigenSnapshot.exists() || !clienteOrigenData) {
              throw new MoveClienteValidationError("No se encontro la informacion del cliente.");
            }

            if (clienteDestinoSnapshot.exists()) {
              throw new MoveClienteValidationError(
                "Ya existe un cliente con esa referencia en la ruta destino.",
              );
            }

            const nombreCliente = String(
              clienteOrigenData.nombreCliente ?? cliente.nombreCliente ?? "Sin nombre",
            ).trim();
            const perfilUid = String(clienteOrigenData.perfilUid ?? cliente.uid);
            const clienteDestinoData = {
              ...clienteOrigenData,
              perfilUid,
            };
            const allClientsDestinoRef = doc(
              db,
              "admin_users",
              adminUid,
              "rutas",
              nuevaRutaUid,
              "allClients",
              perfilUid,
            );
            const nombreRutaOrigen = obtenerNombreRuta(rutaOrigenData.nombreRuta, usuarioRuta.uid);
            const nombreRutaDestino = obtenerNombreRuta(rutaDestinoData.nombreRuta, nuevaRutaUid);

            if (clienteTienePagosRegistrados(clienteOrigenData)) {
              throw new MoveClienteValidationError(
                `No se puede pasar el cliente "${nombreCliente}" para la ruta "${nombreRutaDestino}" porque ya ese cliente consta con movimientos.`,
                {
                  clienteNombre: nombreCliente,
                  rutaNombre: nombreRutaDestino,
                },
              );
            }

            const valorPrestamo = normalizarNumero(clienteOrigenData.valorPrestamo);
            const saldoOrigenNuevo = normalizarNumero(rutaOrigenData.saldoInicial) + valorPrestamo;
            const saldoDestinoNuevo =
              normalizarNumero(rutaDestinoData.saldoInicial) - valorPrestamo;
            const movimientoOrigen = crearMovimientoRutaData({
              clienteUid: cliente.uid,
              descripcion: "traspaso de cliente",
              fecha: fechaMovimiento,
              monto: valorPrestamo,
              origen: "traspaso_cliente",
              responsable: nombreCliente,
              saldoDespues: saldoOrigenNuevo,
              tipo: "ingreso",
            });
            const movimientoDestino = crearMovimientoRutaData({
              clienteUid: cliente.uid,
              descripcion: `cliente recibido de la ruta "${nombreRutaOrigen}"`,
              fecha: fechaMovimiento,
              monto: valorPrestamo,
              origen: "traspaso_cliente_recibido",
              responsable: nombreCliente,
              saldoDespues: saldoDestinoNuevo,
              tipo: "salida",
            });
            const rutaOrigenUpdates = construirActualizacionRutaConMovimiento(
              rutaOrigenData,
              movimientoOrigen,
              saldoOrigenNuevo,
            );
            const rutaDestinoUpdates = construirActualizacionRutaConMovimiento(
              rutaDestinoData,
              movimientoDestino,
              saldoDestinoNuevo,
            );

            transaction.set(movimientoOrigenRef, movimientoOrigen);
            transaction.set(movimientoDestinoRef, movimientoDestino);
            transaction.set(clienteDestinoRef, clienteDestinoData);
            transaction.set(
              allClientsDestinoRef,
              crearAllClientsPayloadDesdeCliente(clienteDestinoData, cliente.uid, fechaMovimiento),
              { merge: true },
            );
            transaction.update(rutaOrigenRef, rutaOrigenUpdates);
            transaction.update(rutaDestinoRef, rutaDestinoUpdates);
            transaction.delete(clienteOrigenRef);

            return {
              clienteTrasladado: {
                uid: cliente.uid,
                ...(clienteDestinoData as TablaClienteItem),
              },
              movimientoDestino,
              movimientoOrigen,
              nombreRutaDestino,
              rutaDestinoUid: nuevaRutaUid,
              rutaOrigenUid: usuarioRuta.uid,
              saldoDestinoNuevo,
              saldoOrigenNuevo,
            };
          },
        );

        setClientes((prevClientes) => prevClientes.filter((client) => client.uid !== cliente.uid));
        setSelectedCliente((prevSelectedCliente) =>
          prevSelectedCliente?.uid === cliente.uid ? null : prevSelectedCliente,
        );
        setRutasData((prevRutas) =>
          prevRutas
            ? prevRutas.map((ruta) => {
                if (ruta.uid === resultadoTransaccion.rutaOrigenUid) {
                  return aplicarMovimientoARutaLocal(
                    ruta,
                    resultadoTransaccion.movimientoOrigen,
                    resultadoTransaccion.saldoOrigenNuevo,
                  );
                }

                if (ruta.uid === resultadoTransaccion.rutaDestinoUid) {
                  return aplicarMovimientoARutaLocal(
                    ruta,
                    resultadoTransaccion.movimientoDestino,
                    resultadoTransaccion.saldoDestinoNuevo,
                  );
                }

                return ruta;
              })
            : prevRutas,
        );
        setUsuarioRuta((previousUsuarioRuta) => {
          if (!previousUsuarioRuta) {
            return previousUsuarioRuta;
          }

          if (previousUsuarioRuta.uid === resultadoTransaccion.rutaOrigenUid) {
            return aplicarMovimientoARutaLocal(
              previousUsuarioRuta,
              resultadoTransaccion.movimientoOrigen,
              resultadoTransaccion.saldoOrigenNuevo,
            );
          }

          if (previousUsuarioRuta.uid === resultadoTransaccion.rutaDestinoUid) {
            return aplicarMovimientoARutaLocal(
              previousUsuarioRuta,
              resultadoTransaccion.movimientoDestino,
              resultadoTransaccion.saldoDestinoNuevo,
            );
          }

          return previousUsuarioRuta;
        });

        if (setUsuarioRuta !== setContextUsuarioRuta) {
          setContextUsuarioRuta((previousUsuarioRuta) => {
            if (!previousUsuarioRuta) {
              return previousUsuarioRuta;
            }

            if (previousUsuarioRuta.uid === resultadoTransaccion.rutaOrigenUid) {
              return aplicarMovimientoARutaLocal(
                previousUsuarioRuta,
                resultadoTransaccion.movimientoOrigen,
                resultadoTransaccion.saldoOrigenNuevo,
              );
            }

            if (previousUsuarioRuta.uid === resultadoTransaccion.rutaDestinoUid) {
              return aplicarMovimientoARutaLocal(
                previousUsuarioRuta,
                resultadoTransaccion.movimientoDestino,
                resultadoTransaccion.saldoDestinoNuevo,
              );
            }

            return previousUsuarioRuta;
          });
        }

        setInfoClientes((prevClientes) => {
          if (!prevClientes) {
            return prevClientes;
          }

          if (contextUsuarioRuta?.uid === resultadoTransaccion.rutaOrigenUid) {
            return prevClientes.filter((client) => client.uid !== cliente.uid);
          }

          if (contextUsuarioRuta?.uid === resultadoTransaccion.rutaDestinoUid) {
            const clienteExistente = prevClientes.some(
              (client) => client.uid === resultadoTransaccion.clienteTrasladado.uid,
            );

            if (!clienteExistente) {
              return [...prevClientes, resultadoTransaccion.clienteTrasladado];
            }

            return prevClientes.map((client) =>
              client.uid === resultadoTransaccion.clienteTrasladado.uid
                ? {
                    ...client,
                    ...resultadoTransaccion.clienteTrasladado,
                  }
                : client,
            );
          }

          return prevClientes;
        });

        // Ademas de los 4 stores patchados arriba, invalidamos las vistas de
        // solo-lectura de ambas rutas (detalle de ruta ajena, allClients del
        // destino, e historial de movimientos/metricas del dia de las dos
        // rutas involucradas), que no se actualizan con los setState de arriba.
        const rutasAfectadas = [
          resultadoTransaccion.rutaOrigenUid,
          resultadoTransaccion.rutaDestinoUid,
        ];

        rutasAfectadas.forEach((rutaUid) => {
          void queryClient.invalidateQueries({
            queryKey: queryKeys.clientesDetalleRuta(adminUid, rutaUid),
          });
          void queryClient.invalidateQueries({
            queryKey: queryKeys.movimientos(adminUid, rutaUid),
          });
          void queryClient.invalidateQueries({
            queryKey: queryKeys.movimientosDelDiaByRuta(adminUid, rutaUid),
          });
        });
        void queryClient.invalidateQueries({
          queryKey: queryKeys.clientes(adminUid, resultadoTransaccion.rutaDestinoUid),
        });
        void queryClient.invalidateQueries({
          queryKey: queryKeys.allClients(adminUid, resultadoTransaccion.rutaDestinoUid),
        });

        toast.success(
          `Cliente movido con exito a la ruta "${resultadoTransaccion.nombreRutaDestino}"`,
        );
      } catch (error) {
        if (error instanceof MoveClienteValidationError) {
          if (error.moveBlockedModal) {
            setMoveBlockedModal(error.moveBlockedModal);
          } else {
            toast.error(error.message);
          }

          return;
        }

        console.error("Error al mover el cliente:", error);
        toast.error("No se pudo mover el cliente");
      } finally {
        setIsMovingClient(false);
      }
    },
    [
      contextUsuarioRuta?.uid,
      isMovingClient,
      queryClient,
      setContextUsuarioRuta,
      setInfoClientes,
      setRutasData,
      setUsuarioRuta,
      userData?.uid,
      usuarioRuta?.adminUid,
      usuarioRuta?.uid,
    ],
  );

  const handleEditClick = useCallback(
    (cliente: TablaClienteItem): void => {
      if (!userData?.isAdmin) {
        return;
      }

      openClienteModal("edit", cliente);
    },
    [openClienteModal, userData?.isAdmin],
  );

  const handleDeleteClick = useCallback(
    (cliente: TablaClienteItem): void => {
      if (!userData?.isAdmin) {
        return;
      }

      openClienteModal("delete", cliente);
    },
    [openClienteModal, userData?.isAdmin],
  );

  const handleAbono = useCallback(
    (cliente: TablaClienteItem): void => {
      openClienteModal("abono", cliente);
    },
    [openClienteModal],
  );

  const handleDetallesCliente = useCallback(
    (cliente: TablaClienteItem): void => {
      openClienteModal("detalles", cliente);
    },
    [openClienteModal],
  );

  const handleSelectedAbonoUpdate = useCallback((updatedAbono: AbonoCliente): void => {
    if (!updatedAbono.uid) {
      return;
    }

    setSelectedCliente((prevSelectedCliente) =>
      prevSelectedCliente?.uid === updatedAbono.uid
        ? ({ ...prevSelectedCliente, ...updatedAbono } as TablaClienteItem)
        : prevSelectedCliente,
    );

    setClientes((prevClientes) =>
      prevClientes.map((cliente) =>
        cliente.uid === updatedAbono.uid ? { ...cliente, ...updatedAbono } : cliente,
      ),
    );
  }, []);

  const isAbono = activeModal === "abono";
  const isModalDelete = activeModal === "delete";
  const verDetallesCliente = activeModal === "detalles";
  const isModalEdit = activeModal === "edit";
  const selectedAbono = isAbono ? selectedCliente : null;
  const selectedDetallesCliente = verDetallesCliente ? selectedCliente : null;

  return {
    closeMoveBlockedModal,
    filteredClients,
    formatearNumeros,
    handleAbono,
    handleButtonClick,
    handleChangeOrder,
    handleDeleteClick,
    handleDetallesCliente,
    handleEditClick,
    handleMoveCliente,
    handleSelectedAbonoUpdate,
    inputValues,
    isAbono,
    isModalDelete,
    isModalEdit,
    isModalPreguntaRenovacion,
    isMovingClient,
    moveBlockedModal,
    rutasFiltradas,
    selectedAbono,
    selectedCliente,
    selectedDetallesCliente,
    setClientes,
    setIsAbono,
    setIsModalDelete,
    setIsModalEdit,
    setIsModalPreguntaRenovacion,
    setSelectedCliente,
    setVerDetallesCliente,
    showAdminActions,
    showMoveColumn,
    userData,
    verDetallesCliente,
  };
};
